import { useCallback, useMemo, useState } from 'react';
import { Link2, Link2Off, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { ResourceSwitch } from '@/components/ResourceSwitch';
import { Button } from '@/components/ui/button';
import {
  ResourceList,
  type ResourceColumn,
  type ResourceFilterDef,
  type ResourceRowAction,
} from '@/components/resources/ResourceList';
import { ResourceCreateDialog } from '@/components/resources/ResourceCreateDialog';
import { ResourceDeleteDialog } from '@/components/resources/ResourceDeleteDialog';
import { api } from '@/lib/api';
import { formatAgo } from '@/lib/ago';
import type { ListQueryConfig } from '@/lib/listQuery';
import { useListQuery } from '@/lib/hooks/useListQuery';
import { usePersistedColumns } from '@/lib/hooks/usePersistedColumns';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { useServerList } from '@/lib/hooks/useServerList';
import type { Volume } from '@/types/volume';
import type { Network } from '@/types/network';

type Kind = 'volume' | 'network';
type Row = Volume | Network;

const LIST_CONFIG: Record<Kind, ListQueryConfig> = {
  volume: {
    filterKeys: ['instance_id', 'endpoint_id', 'driver'],
    numericFilterKeys: ['instance_id', 'endpoint_id'],
    sortableColumns: ['name', 'driver', 'created_at'],
    defaultSortBy: 'created_at',
    defaultSortOrder: 'desc',
  },
  network: {
    filterKeys: ['instance_id', 'endpoint_id', 'driver'],
    numericFilterKeys: ['instance_id', 'endpoint_id'],
    sortableColumns: ['name', 'driver', 'created_at'],
    defaultSortBy: 'created_at',
    defaultSortOrder: 'desc',
  },
};

const COPY: Record<Kind, { title: string; singular: string; description: string; emptyTitle: string; emptyDescription: string }> = {
  volume: {
    title: 'Volumes',
    singular: 'volume',
    description: 'Docker volumes synced from your Portainer instances.',
    emptyTitle: 'No volumes yet',
    emptyDescription: 'Volumes appear here after an instance syncs, or create one.',
  },
  network: {
    title: 'Networks',
    singular: 'network',
    description: 'Docker networks synced from your Portainer instances.',
    emptyTitle: 'No networks yet',
    emptyDescription: 'Networks appear here after an instance syncs, or create one.',
  },
};

// Same options the previous pages offered. Other drivers present in the URL are shown by ResourceList's fallback.
const DRIVERS: Record<Kind, string[]> = {
  volume: ['local', 'nfs', 'tmpfs'],
  network: ['bridge', 'overlay', 'host'],
};

const SWITCH_OPTIONS = [
  { label: 'Volumes', to: '/resources/volumes' },
  { label: 'Networks', to: '/resources/networks' },
];

const fetchInstances = () => api.instances.list();
const fetchEndpoints = () => api.endpoints.listAll();

function createdText(iso: string): { ago: string; full: string } | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return { ago: formatAgo(t), full: new Date(t).toLocaleString() };
}

const COLUMNS: ResourceColumn<Row>[] = [
  { key: 'name', header: 'Name', sortable: true, title: (r) => r.name, cell: (r) => <span className="font-mono text-xs font-medium">{r.name}</span> },
  { key: 'driver', header: 'Driver', sortable: true, cell: (r) => r.driver },
  { key: 'instance', header: 'Instance', cell: (r) => r.instance?.name ?? '—' },
  { key: 'endpoint', header: 'Endpoint', hideBelow: 'md', cell: (r) => r.endpoint?.name ?? '—' },
  {
    key: 'deployment',
    header: 'Deployment-linked',
    hideBelow: 'md',
    cell: (r) => {
      const linked = r.deployment_id !== undefined && r.deployment_id !== null;
      const Icon = linked ? Link2 : Link2Off;
      return (
        <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
          <Icon className="size-3" aria-hidden="true" />
          {linked ? 'Linked' : 'Not linked'}
        </span>
      );
    },
  },
  {
    key: 'created_at',
    header: 'Created',
    sortable: true,
    hideBelow: 'md',
    cell: (r) => {
      const t = createdText(r.created_at);
      return t ? <time dateTime={r.created_at} title={t.full}>{t.ago}</time> : '—';
    },
  },
];
const ALL_KEYS = COLUMNS.map((c) => c.key);

export function VolumeNetworkList({ kind }: { kind: Kind }) {
  const copy = COPY[kind];
  const { query, setFilter, setSort, setPage, setLimit, clearFilters } = useListQuery(LIST_CONFIG[kind], {
    limitStorageKey: `${kind}s.limit`,
  });
  const { rows, total, loading, refreshing, error, refetch } = useServerList<Row>(`${kind}s`, query);
  const instancesP = usePolledData(fetchInstances);
  const endpointsP = usePolledData(fetchEndpoints);
  const [visible, toggleColumn] = usePersistedColumns(`${kind}s.columns`, ALL_KEYS, ALL_KEYS);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Row | null>(null);

  const filters = useMemo<ResourceFilterDef[]>(
    () => [
      { key: 'instance_id', label: 'Instance', allLabel: 'All instances', options: (instancesP.data ?? []).map((i) => ({ value: String(i.id), label: i.name })) },
      { key: 'endpoint_id', label: 'Endpoint', allLabel: 'All endpoints', options: (endpointsP.data ?? []).map((e) => ({ value: String(e.id), label: e.name })) },
      { key: 'driver', label: 'Driver', allLabel: 'All drivers', options: DRIVERS[kind].map((d) => ({ value: d, label: d })) },
    ],
    [instancesP.data, endpointsP.data, kind],
  );

  const actions = useMemo<ResourceRowAction<Row>[]>(
    () => [
      {
        id: 'delete',
        label: (r) => `Delete ${r.name}`,
        icon: Trash2,
        destructive: true,
        enabled: () => true,
        run: (r) => setDeleteTarget(r),
      },
    ],
    [],
  );

  const onPage = useCallback((p: number) => setPage(p, total), [setPage, total]);

  const handleCreate = async (data: { name: string; instance_id: number; endpoint_id: number; driver?: string }) => {
    await (kind === 'volume' ? api.volumes.create(data) : api.networks.create(data));
    toast.success(kind === 'volume' ? 'Volume created' : 'Network created');
    refetch();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await (kind === 'volume' ? api.volumes.delete(deleteTarget.id) : api.networks.delete(deleteTarget.id));
    toast.success(kind === 'volume' ? 'Volume deleted' : 'Network deleted');
    refetch();
  };

  return (
    <div className="space-y-6">
      <PageHeader title={copy.title} description={copy.description} />
      <ResourceSwitch active={`/resources/${kind}s`} label="Volumes and Networks" options={SWITCH_OPTIONS} />
      <ResourceList<Row>
        columns={COLUMNS}
        visibleColumns={visible}
        onToggleColumn={toggleColumn}
        rows={rows}
        total={total}
        getRowKey={(r) => r.id}
        query={query}
        onFilter={setFilter}
        onSort={setSort}
        onPage={onPage}
        onLimit={setLimit}
        onClearFilters={clearFilters}
        filters={filters}
        loading={loading}
        refreshing={refreshing}
        error={error}
        onRetry={refetch}
        tableLabel={copy.title}
        rowActions={actions}
        toolbarExtra={
          <Button type="button" onClick={() => setCreateOpen(true)}>
            <Plus aria-hidden="true" />
            Create {copy.singular}
          </Button>
        }
        empty={{ title: copy.emptyTitle, description: copy.emptyDescription }}
      />
      <ResourceCreateDialog
        resourceType={kind}
        instances={instancesP.data ?? []}
        endpoints={endpointsP.data ?? []}
        onSubmit={handleCreate}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <ResourceDeleteDialog
        resourceType={kind}
        resource={deleteTarget}
        onSubmit={handleDelete}
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
